import React, { useState } from 'react'
import { Helmet } from 'react-helmet'
import { IMaskInput } from 'react-imask'

import { UserTrialSimcardsService } from '../../../services/http/userTrialSimcards.service.js'
import { useGlobal } from '../../../stores/useGlobal.js'
import ScrollArea from '../../Mantine/ScrollArea'

import * as S from './styles.js'

export default function UserData({ onSubmit }) {
  const { postFormUser, postSendEmail } = UserTrialSimcardsService
  const { setIsLoadingGlobal, setMostrarNotificacao } = useGlobal()
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    emailConfirmation: '',
    company: '',
    checkValue: false
  })
  const [emailError, setEmailError] = useState('')
  const [confirmEmailError, setConfirmEmailError] = useState(false)
  const [phoneError, setPhoneError] = useState('')
  const [nameError, setNameError] = useState('')
  const [companyError, setCompanyError] = useState('')
  const [checkValueError, setCheckValueError] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(false)

  const validateEmail = (value, forceError = false, message = '') => {
    if (forceError) return message
    else if (!/^\S+@\S+$/.test(value)) return 'Email inválido'
    return ''
  }

  const validateConfirmEmail = (value) => {
    if (value !== formData.email) {
      return true
    }
    return false
  }

  const validateName = (value) => {
    if (value.length < 3) {
      return 'Nome inválido'
    }
    return ''
  }

  const validateCompany = (value) => {
    if (value.length < 3) {
      return 'Empresa inválida'
    }
    return ''
  }

  const validatePhone = (value) => {
    const phoneNumber = value.replace(/\D/g, '')
    if (phoneNumber.length < 10 || phoneNumber.length > 11) {
      return 'Telefone inválido'
    }
    return ''
  }

  const validateCheckValue = (value) => {
    if (!value) {
      return 'Você precisa aceitar os termos para continuar'
    }
    return ''
  }

  const handleEmailChange = (event) => {
    const value = event.target.value
    setFormData((prev) => ({ ...prev, email: value }))
    if (isSubmitted) {
      setEmailError(validateEmail(value))
    } else {
      setEmailError('')
    }
  }

  const handleConfirmEmailChange = (event) => {
    const value = event.target.value
    setFormData((prev) => ({ ...prev, emailConfirmation: value }))
    if (isSubmitted) {
      setConfirmEmailError(validateConfirmEmail(value))
    } else {
      setConfirmEmailError('')
    }
  }

  const handleNameChange = (event) => {
    const value = event.target.value
    setFormData((prev) => ({ ...prev, name: value }))
    if (isSubmitted) {
      setNameError(validateName(value))
    } else {
      setNameError('')
    }
  }

  const handleCompanyChange = (event) => {
    const value = event.target.value
    setFormData((prev) => ({ ...prev, company: value }))
    if (isSubmitted) {
      setCompanyError(validateCompany(value))
    } else {
      setCompanyError('')
    }
  }

  const handlePhoneChange = (event) => {
    const value = event.target.value
    setFormData((prev) => ({ ...prev, phone: value }))
    if (isSubmitted) {
      setPhoneError(validatePhone(value))
    } else {
      setPhoneError('')
    }
  }

  const handleCheckValueChange = (event) => {
    const value = event.target.checked
    setFormData((prev) => ({ ...prev, checkValue: value }))
    if (isSubmitted) {
      setCheckValueError(validateCheckValue(value))
    } else {
      setCheckValueError('')
    }
  }

  const handleBlur = (field) => (event) => {
    const value = event.target.value
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (isSubmitted) {
      switch (field) {
        case 'email':
          setEmailError(validateEmail(value))
          break
        case 'emailConfirmation':
          setConfirmEmailError(validateConfirmEmail(value))
          break
        case 'name':
          setNameError(validateName(value))
          break
        case 'company':
          setCompanyError(validateCompany(value))
          break
        case 'phone':
          setPhoneError(validatePhone(value))
          break
        default:
          break
      }
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setIsSubmitted(true)

    const emailValidation = validateEmail(formData.email)
    const confirmEmailValidation = validateConfirmEmail(
      formData.emailConfirmation
    )
    const nameValidation = validateName(formData.name)
    const companyValidation = validateCompany(formData.company)
    const phoneValidation = validatePhone(formData.phone)
    const checkValueValidation = validateCheckValue(formData.checkValue)

    setEmailError(emailValidation)
    setConfirmEmailError(confirmEmailValidation)
    setNameError(nameValidation)
    setCompanyError(companyValidation)
    setPhoneError(phoneValidation)
    setCheckValueError(checkValueValidation)

    const hasErrors =
      emailValidation ||
      confirmEmailValidation ||
      nameValidation ||
      companyValidation ||
      phoneValidation ||
      checkValueValidation

    if (!hasErrors) {
      setIsLoadingGlobal(true)
      try {
        const response = await postFormUser({
          ...formData
        })

        if (response.id_insert) await postSendEmail(response.id_insert)

        onSubmit(formData)
      } catch (error) {
        const { message } = error

        setMostrarNotificacao({
          show: true,
          type: 'error',
          autoHide: 5000,
          title: 'Falha ao cadastrar usuário',
          message: message
        })

        if (message === 'E-mail já em uso na plataforma!') {
          setEmailError(validateEmail(formData.email, true, message))
        }
      } finally {
        setIsLoadingGlobal(false)
      }
    }
  }

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <S.Container>
        <ScrollArea type="always">
          <S.ContentWrapper>
            <S.Title maxWidth="695px" fontSize="34px">
              Você ganhou 1 mês grátis para testar a nossa conectividade IoT.
            </S.Title>

            <S.Paragraph>
              Preencha o formulário abaixo com seus dados e siga as instruções.{' '}
              <br />
              <b>
                Você está prestes a experimentar uma conectividade robusta e
                estável, integrada à plataforma de gestão: o V.EYE.
              </b>
            </S.Paragraph>

            <S.Box>
              <S.Form onSubmit={handleSubmit} autoComplete="off">
                <S.Input
                  withAsterisk={!!nameError}
                  label="Nome"
                  placeholder="Seu nome"
                  component={IMaskInput}
                  mask={/^[A-Za-zÀ-ÿ\s]*$/}
                  value={formData.name}
                  maxLength={100}
                  error={nameError}
                  onChange={handleNameChange}
                  onBlur={handleBlur('name')}
                  spellCheck={false}
                />

                <S.Input
                  withAsterisk={!!phoneError}
                  label="Telefone"
                  placeholder="Seu telefone"
                  component={IMaskInput}
                  mask="(00) 000000000"
                  maxLength={14}
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  onBlur={handleBlur('phone')}
                  error={phoneError}
                />

                <S.Input
                  withAsterisk={!!emailError}
                  label="E-mail corporativo"
                  placeholder="Seu melhor e-mail"
                  description="Atenção: esse será o e-mail cadastrado para gerenciar o SIM card."
                  value={formData.email}
                  onChange={handleEmailChange}
                  onBlur={handleBlur('email')}
                  error={emailError}
                />

                <S.CustomInputWrapper>
                  <S.CustomInputLabel>
                    Confirmação do e-mail
                    {confirmEmailError && (
                      <span className="error-message"> *</span>
                    )}
                  </S.CustomInputLabel>
                  <div>
                    <S.Input
                      value={formData.emailConfirmation}
                      onChange={handleConfirmEmailChange}
                      onBlur={handleBlur('emailConfirmation')}
                      error={confirmEmailError}
                    />
                    <S.CustomPlaceholder
                      error={confirmEmailError}
                      hasValue={!!formData.emailConfirmation}
                    >
                      Digite novamente
                    </S.CustomPlaceholder>
                  </div>
                </S.CustomInputWrapper>

                <S.Input
                  withAsterisk={!!companyError}
                  label="Empresa"
                  placeholder="O nome da sua empresa"
                  value={formData.company}
                  onChange={handleCompanyChange}
                  onBlur={handleBlur('company')}
                  error={companyError}
                />

                <S.CheckboxContainer>
                  <S.Checkbox
                    error={checkValueError}
                    checked={formData.checkValue}
                    onChange={handleCheckValueChange}
                  />
                  <S.CheckboxDescription>
                    Ao submeter suas informações, você concorda em receber
                    notificações da Virtueyes IoT e concorda com a coleta de
                    dados referenciada na{' '}
                    <S.Link
                      href="https://virtueyes.com.br/politica-de-privacidade/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Política de Privacidade
                    </S.Link>{' '}
                    atualizada da Virtueyes IoT Solutions.
                  </S.CheckboxDescription>
                </S.CheckboxContainer>

                <S.Group position="center" mt="md">
                  <S.Button fullWidth type="submit">
                    Cadastrar-se
                  </S.Button>
                </S.Group>
              </S.Form>
            </S.Box>
          </S.ContentWrapper>
        </ScrollArea>
      </S.Container>
    </>
  )
}
